import { useTranslation } from "react-i18next";
import { useNotes, UseNotesProps } from "./useNotes";
import { SimpleErrorNotification, SimpleLoader } from "shared/components/atoms";
import { Box, Grid } from "styled-system/jsx";
import { Note } from "../Note/Note";
import { EmptyState } from "shared/components/root";
import { Icon, LinkButton } from "@ttc3k/trekker";
import { NavArrowDown, NavArrowUp } from "iconoir-react";
import { Collapsible } from "shared/components/molecules";
import { css } from "styled-system/css";
import { Dispatch, SetStateAction, useEffect } from "react";

type NotesProps = {
  forceSingleColumn?: boolean;
  viewOnly?: boolean;
  setNoteCount?: Dispatch<SetStateAction<number>>;
} & UseNotesProps;

export const Notes = ({
  _entityId,
  type,
  forceSingleColumn = false,
  viewOnly = false,
  getNoteCreatingState,
  setNoteCount,
}: NotesProps) => {
  const { t } = useTranslation();

  const {
    error,
    isCreatingNote,
    isSomeUnresolvedNotes,
    loading,
    loadingNoteId,
    resolvedNotes,
    showResolvedNotes,
    unResolvedNotes,
    onRemoveNote,
    onResolveNote,
    onUpdateNoteMessage,
    onCreateNote,
    setIsCreatingNote,
    setShowResolvedNotes,
  } = useNotes({
    _entityId,
    type,
    getNoteCreatingState,
  });

  useEffect(
    () =>
      setNoteCount?.(
        (unResolvedNotes?.length ?? 0) + (resolvedNotes?.length ?? 0),
      ),
    [unResolvedNotes, resolvedNotes, setNoteCount],
  );

  if (loading) return <SimpleLoader />;

  if (error) return <SimpleErrorNotification />;

  return (
    <>
      <Grid
        gap={"300"}
        className={css({
          gridTemplateColumns:
            (isSomeUnresolvedNotes || isCreatingNote) && !forceSingleColumn
              ? {
                  base: "1fr",
                  xl: "repeat(2, 1fr)",
                }
              : "1fr",
        })}
      >
        {/* Creation of new note: */}
        {isCreatingNote && (
          <Note
            onEditSave={onCreateNote}
            loading={loadingNoteId === "newNote"}
            viewOnly={viewOnly}
            onCancelEditing={() => setIsCreatingNote(false)}
          />
        )}

        {/* Unresolved notes */}
        {isSomeUnresolvedNotes ? (
          <>
            {unResolvedNotes.map((note) => (
              <Note
                note={note}
                key={note._id}
                viewOnly={viewOnly}
                onEditSave={(msg) => onUpdateNoteMessage(note._id, msg)}
                onDeleteNote={() => onRemoveNote(note._id)}
                onResolveNoteClick={() => onResolveNote(note._id)}
                loading={loadingNoteId === note._id}
              />
            ))}
          </>
        ) : (
          <>
            {!isCreatingNote && (
              <EmptyState
                text={t("core:NOTES.EMPTY")}
                ctaButtonProps={
                  !viewOnly && {
                    children: t("core:NOTES.CREATE"),
                    size: "md",
                    onClick: () => setIsCreatingNote(true),
                  }
                }
                p={viewOnly ? "400" : "600"}
              />
            )}
          </>
        )}
      </Grid>

      {/* Resolved notes */}
      {resolvedNotes && resolvedNotes.length > 0 && (
        <>
          <Box>
            <LinkButton onClick={() => setShowResolvedNotes((prev) => !prev)}>
              {showResolvedNotes
                ? t("core:NOTES.HIDE_RESOLVED")
                : t("core:NOTES.SHOW_RESOLVED")}
              &nbsp;
              <Icon Element={showResolvedNotes ? NavArrowUp : NavArrowDown} />
            </LinkButton>
          </Box>
          <Collapsible open={showResolvedNotes}>
            <Grid
              gap={"300"}
              gridTemplateColumns={{
                base: "1fr",
                xl: !forceSingleColumn ? "repeat(2, 1fr)" : "1fr",
              }}
            >
              {resolvedNotes.map((note) => (
                <Note note={note} key={note._id} viewOnly={viewOnly} />
              ))}
            </Grid>
          </Collapsible>
        </>
      )}
    </>
  );
};
